import React, { useState, useMemo, useRef, useCallback, useEffect } from 'react'
import { formatCurrencyAmount } from '../../utils/currencyUtils'
import {
	Box,
	Typography,
	Button,
	Dialog,
	DialogTitle,
	DialogContent,
	CircularProgress,
	IconButton,
	useMediaQuery,
	useTheme
} from '@mui/material'
import CalendarTodayIcon from '@mui/icons-material/CalendarToday'
import ContentCutIcon from '@mui/icons-material/ContentCut'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import PetsIcon from '@mui/icons-material/Pets'
import GroupIcon from '@mui/icons-material/Group'
import PersonIcon from '@mui/icons-material/Person'
import EmailIcon from '@mui/icons-material/Email'
import PhoneIcon from '@mui/icons-material/Phone'
import CloseIcon from '@mui/icons-material/Close'
import DrawIcon from '@mui/icons-material/Draw'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import { Document, Page, pdfjs } from 'react-pdf'
import 'react-pdf/dist/Page/AnnotationLayer.css'
import 'react-pdf/dist/Page/TextLayer.css'
import SignatureCanvas from 'react-signature-canvas'
import { supabase } from '../../utils/supabase'
import { v4 as uuidv4 } from 'uuid'
import dayjs from 'dayjs'
import { buildFullName } from '../../utils/clientUtils'

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`

// Icon Row Component
const IconRow = ({ icon, text }) => (
	<Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', width: '100%' }}>
		{icon}
		<Typography sx={{ fontFamily: 'Inter', fontWeight: 400, fontSize: '14px', lineHeight: 1.21, color: '#2D3748' }}>
			{text}
		</Typography>
	</Box>
)

// Main ConfirmationStep Component
function ConfirmationStep({
	clientInfo,
	pets,
	petServices,
	services,
	addOns,
	selectedDate,
	selectedTime,
	selectedEmployee,
	employees,
	totalDuration,
	onRequestBooking,
	onBack,
	loading = false,
	merchant,
	// Agreement props
	agreements = [],
	clientInitialAgreementSigned = false,
	signedOneTimeAgreementIds = [],
	merchantId,
	onAgreementSigned, // (agreementId, { signedAgreementId, storagePath }) => void
}) {
	const theme = useTheme()
	const isMobile = useMediaQuery(theme.breakpoints.down('md'))

	const agreementsToSign = useMemo(() => {
		return agreements.filter(a => {
			if (!a.enabled) return false
			if (!clientInitialAgreementSigned) {
				return a.frequency === 'every_booking' || a.frequency === 'once'
			}
			if (a.frequency === 'every_booking') return true
			if (a.frequency === 'once') {
				if (signedOneTimeAgreementIds.includes(a.id)) return false
				return !!a.required_for_existing_clients
			}
			return false
		})
	}, [agreements, clientInitialAgreementSigned, signedOneTimeAgreementIds])

	// Local signed state: { [agreementId]: boolean }
	const [localSigned, setLocalSigned] = useState({})

	const allAgreementsSigned = useMemo(() => {
		return agreementsToSign.length === 0 || agreementsToSign.every(a => localSigned[a.id])
	}, [agreementsToSign, localSigned])

	// Sign dialog state
	const [signDialogOpen, setSignDialogOpen] = useState(false)
	const [activeAgreement, setActiveAgreement] = useState(null)
	const [pdfUrl, setPdfUrl] = useState(null)
	const [pdfNumPages, setPdfNumPages] = useState(null)
	const [pdfLoading, setPdfLoading] = useState(false)
	const [signing, setSigning] = useState(false)
	const [sigError, setSigError] = useState('')
	const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false)
	const sigPadRef = useRef(null)
	const contentScrollRef = useRef(null)

	// Auto-unlock when content doesn't require scrolling
	useEffect(() => {
		if (!signDialogOpen || hasScrolledToBottom) return
		// Use a small timeout so the DOM has painted the content
		const timer = setTimeout(() => {
			const el = contentScrollRef.current
			if (el && el.scrollHeight <= el.clientHeight + 24) {
				setHasScrolledToBottom(true)
			}
		}, 100)
		return () => clearTimeout(timer)
	}, [signDialogOpen, hasScrolledToBottom, pdfNumPages, activeAgreement])

	const handleContentScroll = useCallback((e) => {
		if (hasScrolledToBottom) return
		const el = e.currentTarget
		// Allow a 24px tolerance for subpixel rounding
		if (el.scrollTop + el.clientHeight >= el.scrollHeight - 24) {
			setHasScrolledToBottom(true)
		}
	}, [hasScrolledToBottom])

	const handleOpenSign = useCallback(async (agreement) => {
		setActiveAgreement(agreement)
		setPdfUrl(null)
		setPdfNumPages(null)
		setSigError('')
		setHasScrolledToBottom(false)
		setSignDialogOpen(true)

		if (agreement.content_path) {
			setPdfLoading(true)
			try {
				// Download via storage API (works with open SELECT policy + anon key)
				const { data: fileData, error } = await supabase.storage
					.from('agreements')
					.download(agreement.content_path)
				if (error) throw error
				setPdfUrl(URL.createObjectURL(fileData))
			} catch (err) {
				console.error('Failed to load PDF:', err)
			} finally {
				setPdfLoading(false)
			}
		}
	}, [])

	const handleCloseSign = () => {
		setSignDialogOpen(false)
		setActiveAgreement(null)
		if (pdfUrl && activeAgreement?.content_path) {
			URL.revokeObjectURL(pdfUrl)
			setPdfUrl(null)
		}
		sigPadRef.current?.clear()
		setSigError('')
		setHasScrolledToBottom(false)
	}

	const handleSignSubmit = async () => {
		if (!activeAgreement) return
		if (sigPadRef.current?.isEmpty()) {
			setSigError('Please draw your signature above before submitting.')
			return
		}
		setSigning(true)
		setSigError('')
		try {
			const signedAgreementId = uuidv4()
			const storagePath = `${merchantId}/${signedAgreementId}.png`

			// Convert canvas to blob
			const dataUrl = sigPadRef.current.toDataURL('image/png')
			const blob = await fetch(dataUrl).then(r => r.blob())

			// Upload signature image
			const { error: uploadErr } = await supabase.storage
				.from('agreement-signatures')
				.upload(storagePath, blob, { contentType: 'image/png' })
			if (uploadErr) throw uploadErr

			// Notify parent (DB insert happens in createBookings after client is created)
			onAgreementSigned?.(activeAgreement.id, { signedAgreementId, storagePath })

			// Mark locally
			setLocalSigned(prev => ({ ...prev, [activeAgreement.id]: true }))
			handleCloseSign()
		} catch (err) {
			setSigError('Failed to save signature: ' + err.message)
		} finally {
			setSigning(false)
		}
	}

	const formatCurrency = (cents) => formatCurrencyAmount(cents, merchant?.currency)

	const formatDateTime = () => {
		if (!selectedDate || !selectedTime) return ''
		const date = dayjs(selectedDate)
		const time = dayjs(`2000-01-01 ${selectedTime}`).format('hh:mm A')
		return `${date.format('ddd, MMM D')}, ${time}`
	}

	const getServiceNames = () => {
		const serviceNames = new Set()
		petServices.forEach(ps => {
			const service = services.find(s => s.id === ps.serviceId)
			if (service) serviceNames.add(service.name)
		})
		return Array.from(serviceNames).join(', ')
	}

	const getPetNames = () => pets.map(p => p.name).filter(Boolean).join(', ') || 'Your pets'

	const getEmployeeName = () => {
		if (!selectedEmployee) return 'Any Professional'
		const emp = employees.find(e => e.id === selectedEmployee)
		return emp ? `${emp.first_name} ${emp.last_name}` : 'Any Professional'
	}

	const calculateSubtotal = () => {
		let total = 0
		petServices.forEach(ps => {
			const service = services.find(s => s.id === ps.serviceId)
			if (service) total += service.price || 0
			;(ps.addOnIds || []).forEach(addOnId => {
				const addOn = addOns.find(a => a.id === addOnId)
				if (addOn) total += addOn.price || 0
			})
		})
		return total
	}

	const formatPhoneDisplay = (phone) => {
		if (!phone || phone.length !== 10) return phone
		return `+1 (${phone.slice(0, 3)}) ${phone.slice(3, 6)}-${phone.slice(6)}`
	}

	const iconStyle = { width: 16, height: 16, color: '#2D3748' }

	return (
		<Box sx={{ display: 'flex', flexDirection: 'column', gap: '18px', width: '100%' }}>
			{/* Section Title */}
			<Typography sx={{ fontFamily: 'Inter', fontWeight: 600, fontSize: isMobile ? '24px' : '32px', lineHeight: 1.125, color: '#2D3748' }}>
				Confirm your booking
			</Typography>

			{/* Main Card */}
			<Box sx={{ display: 'flex', flexDirection: 'column', gap: '18px', padding: '16px', bgcolor: '#FFFFFF', borderRadius: '12px', boxShadow: '0px 4px 4px -4px rgba(12, 12, 13, 0.05), 0px 16px 32px -4px rgba(12, 12, 13, 0.1)' }}>
				{/* Booking + Info Cards */}
				<Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: '18px' }}>
					{/* Booking Summary */}
					<Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px', padding: '16px 24px', bgcolor: '#FFFFFF', border: '1px solid #CBD5E0', borderRadius: '12px', flex: 1 }}>
						<Typography sx={{ fontFamily: 'Inter', fontWeight: 600, fontSize: '20px', lineHeight: 1.2, color: '#2D3748' }}>
							Booking summary
						</Typography>
						<IconRow icon={<CalendarTodayIcon sx={iconStyle} />} text={formatDateTime()} />
						<IconRow icon={<ContentCutIcon sx={iconStyle} />} text={getServiceNames()} />
						<IconRow icon={<AccessTimeIcon sx={iconStyle} />} text={`${totalDuration} minutes`} />
						<IconRow icon={<PetsIcon sx={iconStyle} />} text={getPetNames()} />
						<IconRow icon={<GroupIcon sx={iconStyle} />} text={getEmployeeName()} />
					</Box>

					{/* My Information */}
					<Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px', padding: '16px 24px', bgcolor: '#FFFFFF', border: '1px solid #CBD5E0', borderRadius: '12px', flex: 1 }}>
						<Typography sx={{ fontFamily: 'Inter', fontWeight: 600, fontSize: '20px', lineHeight: 1.2, color: '#2D3748' }}>
							My information
						</Typography>
						<IconRow icon={<PersonIcon sx={iconStyle} />} text={buildFullName(clientInfo.name, clientInfo.lastName)} />
						<IconRow icon={<EmailIcon sx={iconStyle} />} text={clientInfo.email} />
						<IconRow icon={<PhoneIcon sx={iconStyle} />} text={formatPhoneDisplay(clientInfo.phone)} />
					</Box>
				</Box>

				{/* Divider */}
				<Box sx={{ width: '100%', height: '1px', bgcolor: '#CBD5E0' }} />

				{/* Subtotal */}
				<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
					<Typography sx={{ fontFamily: 'Inter', fontWeight: 600, fontSize: '16px', lineHeight: 2.25, color: '#2D3748' }}>
						Subtotal
					</Typography>
					<Typography sx={{ fontFamily: 'Inter', fontWeight: 600, fontSize: '16px', lineHeight: 2.25, color: '#2D3748' }}>
						{formatCurrency(calculateSubtotal())}
					</Typography>
				</Box>

				{/* Agreements to sign */}
				{agreementsToSign.length > 0 && (
					<Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
						{agreementsToSign.map((agreement) => {
							const isSigned = localSigned[agreement.id]
							return (
								<Box
									key={agreement.id}
									onClick={() => !isSigned && handleOpenSign(agreement)}
									sx={{
										display: 'flex',
										alignItems: 'center',
										gap: '10px',
										p: '10px 14px',
										border: `1px solid ${isSigned ? '#38A169' : '#CBD5E0'}`,
										borderRadius: '10px',
										bgcolor: isSigned ? '#F0FFF4' : '#FAFAFA',
										cursor: isSigned ? 'default' : 'pointer',
										transition: 'all 0.15s ease',
										'&:hover': isSigned ? {} : { borderColor: '#9F7AEA', bgcolor: '#FAF5FF' },
									}}
								>
									{isSigned ? (
										<CheckCircleIcon sx={{ fontSize: 20, color: '#38A169', flexShrink: 0 }} />
									) : (
										<DrawIcon sx={{ fontSize: 20, color: '#9F7AEA', flexShrink: 0 }} />
									)}
									<Box sx={{ flex: 1 }}>
										<Typography sx={{ fontFamily: 'Inter', fontWeight: 600, fontSize: '13px', color: isSigned ? '#276749' : '#2D3748' }}>
											{agreement.title}
										</Typography>
										<Typography sx={{ fontFamily: 'Inter', fontWeight: 400, fontSize: '11px', color: isSigned ? '#38A169' : '#9F7AEA', mt: '2px' }}>
											{isSigned ? 'Signed ✓' : 'Click to read & sign'}
										</Typography>
									</Box>
								</Box>
							)
						})}
					</Box>
				)}

				{/* Request Booking Button */}
				<Button
					onClick={onRequestBooking}
					disabled={loading || !allAgreementsSigned}
					fullWidth
					sx={{
						py: '16px',
						bgcolor: '#E9D8FD',
						border: '1px solid #9F7AEA',
						borderRadius: '12px',
						color: '#2D3748',
						fontFamily: 'Inter',
						fontWeight: 600,
						fontSize: '20px',
						lineHeight: 1.21,
						textTransform: 'none',
						boxShadow: '0px 4px 4px 0px rgba(0, 0, 0, 0.25)',
						'&:hover': { bgcolor: '#D6BCFA' },
						'&:disabled': { bgcolor: '#E2E8F0', borderColor: '#CBD5E0', color: '#A0AEC0' }
					}}
				>
					{loading ? 'Processing...' : 'Request Booking'}
				</Button>

				{/* Terms text when no agreements */}
				{agreementsToSign.length === 0 && (
					<Typography sx={{ fontFamily: 'Inter', fontWeight: 400, fontSize: '11px', lineHeight: 1.21, color: '#4A5568', textAlign: 'center' }}>
						By booking an appointment you agree to the{' '}
						<Box component="span" sx={{ fontWeight: 600, color: '#2D3748' }}>Terms of Service</Box>
						{' '}and the{' '}
						<Box component="span" sx={{ fontWeight: 600, color: '#2D3748' }}>Privacy Policy</Box>.
					</Typography>
				)}
			</Box>

			{/* Back Button */}
			<Button
				onClick={onBack}
				disabled={loading}
				sx={{
					width: '100px',
					padding: '12px 16px',
					bgcolor: '#FFFFFF',
					border: '1px solid #9F7AEA',
					borderRadius: '6px',
					color: '#9F7AEA',
					fontFamily: 'Inter',
					fontWeight: 600,
					fontSize: '14px',
					lineHeight: 1.21,
					textTransform: 'none',
					'&:hover': { bgcolor: '#F7FAFC' }
				}}
			>
				Back
			</Button>

			{/* Sign Agreement Dialog */}
			<Dialog
				open={signDialogOpen}
				onClose={handleCloseSign}
				maxWidth={false}
				PaperProps={{
					sx: { width: isMobile ? '100%' : 580, maxHeight: '92vh', borderRadius: '16px', m: isMobile ? 1 : 2, display: 'flex', flexDirection: 'column' }
				}}
			>
				<DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #E2E8F0', pb: 2 }}>
					<Box>
						<Typography sx={{ fontFamily: 'Inter', fontWeight: 600, fontSize: '18px', color: '#2D3748' }}>
							{activeAgreement?.title || 'Agreement'}
						</Typography>
						<Typography sx={{ fontFamily: 'Inter', fontWeight: 400, fontSize: '12px', color: '#718096', mt: '2px' }}>
							Please read and sign below to continue
						</Typography>
					</Box>
					<IconButton onClick={handleCloseSign} size="small">
						<CloseIcon />
					</IconButton>
				</DialogTitle>

				<DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
					{/* Agreement Content */}
					<Box
						ref={contentScrollRef}
						onScroll={handleContentScroll}
						sx={{ flex: 1, overflow: 'auto', p: '16px 20px', borderBottom: '1px solid #E2E8F0', minHeight: 0 }}
					>
						{activeAgreement?.content_path ? (
							pdfLoading ? (
								<Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
									<CircularProgress size={28} sx={{ color: '#9F7AEA' }} />
								</Box>
							) : pdfUrl ? (
								<Document
									file={pdfUrl}
									onLoadSuccess={({ numPages }) => setPdfNumPages(numPages)}
									loading={<Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={24} sx={{ color: '#9F7AEA' }} /></Box>}
								>
									{Array.from({ length: pdfNumPages || 0 }, (_, i) => (
										<Box key={i + 1} sx={{ mb: '8px' }}>
											<Page
												pageNumber={i + 1}
												width={isMobile ? 300 : 520}
												renderTextLayer={false}
												renderAnnotationLayer={false}
											/>
										</Box>
									))}
								</Document>
							) : (
								<Typography sx={{ fontFamily: 'Inter', fontSize: '14px', color: '#718096', textAlign: 'center', py: 3 }}>
									Unable to load PDF. Please contact the merchant.
								</Typography>
							)
						) : (
							<Box
								sx={{ fontFamily: 'Inter', fontSize: '14px', lineHeight: 1.7, color: '#4A5568', '& h1,& h2,& h3': { fontWeight: 600, mb: 1 }, '& p': { mb: 1 }, '& ul,& ol': { pl: 2, mb: 1 } }}
								dangerouslySetInnerHTML={{ __html: activeAgreement?.content || '' }}
							/>
						)}
					</Box>

					{/* Signature Area */}
					<Box sx={{ p: '16px 20px', bgcolor: '#FAFAFA' }}>
						<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: '8px' }}>
							<Typography sx={{ fontFamily: 'Inter', fontWeight: 600, fontSize: '13px', color: '#2D3748' }}>
								Your signature
							</Typography>
							<Button
								size="small"
								onClick={() => sigPadRef.current?.clear()}
								sx={{ textTransform: 'none', fontFamily: 'Inter', fontSize: '12px', color: '#718096', p: '2px 8px', minWidth: 0 }}
							>
								Clear
							</Button>
						</Box>
						<Box sx={{ border: '1.5px solid #CBD5E0', borderRadius: '8px', bgcolor: '#FFFFFF', overflow: 'hidden', touchAction: 'none' }}>
							<SignatureCanvas
								ref={sigPadRef}
								penColor="#1A202C"
								canvasProps={{
									style: { width: '100%', height: isMobile ? 100 : 120, display: 'block' }
								}}
							/>
						</Box>
						<Typography sx={{ fontFamily: 'Inter', fontSize: '11px', color: '#A0AEC0', mt: '6px' }}>
							Draw your signature in the box above
						</Typography>

						{sigError && (
							<Typography sx={{ fontFamily: 'Inter', fontSize: '12px', color: '#E53E3E', mt: '6px' }}>
								{sigError}
							</Typography>
						)}

						{!hasScrolledToBottom && (
							<Typography sx={{ fontFamily: 'Inter', fontSize: '11px', color: '#A0AEC0', mt: '8px', textAlign: 'center' }}>
								↓ Scroll to the bottom to enable signing
							</Typography>
						)}

						<Button
							onClick={handleSignSubmit}
							disabled={signing || !hasScrolledToBottom}
							fullWidth
							sx={{
								mt: '8px',
								py: '12px',
								bgcolor: '#9F7AEA',
								borderRadius: '8px',
								color: '#FFFFFF',
								fontFamily: 'Inter',
								fontWeight: 600,
								fontSize: '15px',
								textTransform: 'none',
								'&:hover': { bgcolor: '#805AD5' },
								'&:disabled': { bgcolor: '#CBD5E0', color: '#A0AEC0' },
							}}
						>
							{signing ? <CircularProgress size={20} sx={{ color: '#FFFFFF' }} /> : 'I agree & sign'}
						</Button>
					</Box>
				</DialogContent>
			</Dialog>
		</Box>
	)
}

export default ConfirmationStep
